import { useState } from "react";
import {
  type IDataRow,
  downloadMolecularData,
  downloadSelectedMolecules,
} from "@/api/molecularData";
import {
  useGetMolecularData,
  useDataFilters,
  useSelectedMolecules,
  useDownloadWithProgress,
  useSyncSelectionWithFilters,
} from "./hooks";
import {
  VirusFilterBar,
  MolecularDataTable,
  FilterPanel,
  DataPageHeader,
  PaginationFooter,
  ActiveFiltersBar,
  SelectedMoleculesBar,
  DataPageError,
  DataPageSkeleton,
  UpdatingIndicator,
} from "./_components";

const BSAD_ID_KEY = "BSA-DB ID";

function parseColumns(
  raw: Array<{ key: string; name: string }>,
): { key: string; displayName: string }[] {
  return raw.map((col) => ({ key: col.key, displayName: col.name }));
}

export function DataPage() {
  const {
    selectedViruses,
    toggleVirus,
    clearViruses,
    panelState,
    applyFilterPanel,
    clearAllFilters,
    filters,
    activeFilterCount,
    activeFilterChips,
  } = useDataFilters();
  const { selectedIds, toggleSelection, clearSelection, pruneToVisible } =
    useSelectedMolecules();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const filteredDownload = useDownloadWithProgress(downloadMolecularData);
  const selectedDownload = useDownloadWithProgress(downloadSelectedMolecules);

  const { data, isPending, isError, isFetching } = useGetMolecularData({
    filters,
    page,
    pageSize,
  });

  // A molecule that no longer matches the active filters can't stay selected.
  useSyncSelectionWithFilters(filters, data?.data, isFetching, pruneToVisible);

  const handleVirusToggle = (virusName: string) => {
    toggleVirus(virusName);
    setPage(1);
  };

  const handleClearViruses = () => {
    clearViruses();
    setPage(1);
  };

  const handleApplyFilterPanel: typeof applyFilterPanel = (next) => {
    applyFilterPanel(next);
    setPage(1);
  };

  const handleClearAllFilters = () => {
    clearAllFilters();
    setPage(1);
  };

  const handlePageSizeChange = (val: unknown) => {
    const option = val as { value: string } | null;
    if (!option) return;
    setPageSize(Number(option.value));
    setPage(1);
  };

  const handleRowClick = (row: IDataRow) => {
    const id = row[BSAD_ID_KEY];
    if (typeof id === "string") toggleSelection(id);
  };

  if (isPending) return <DataPageSkeleton />;
  if (isError) return <DataPageError />;

  const columns = parseColumns(data.columns);
  const { metadata, viruses, rangeBounds } = data;

  return (
    <div className="flex h-[calc(100vh-6rem)] flex-col gap-6 overflow-hidden bg-backgroundPrimary p-4 md:p-8">
      <DataPageHeader
        total={metadata.total}
        activeFilterCount={activeFilterCount}
        isDownloading={filteredDownload.isDownloading}
        downloadProgress={filteredDownload.progress}
        onOpenFilters={() => setIsFilterPanelOpen(true)}
        onDownload={() => filteredDownload.trigger(filters)}
      />

      <ActiveFiltersBar
        chips={activeFilterChips}
        onClearAll={handleClearAllFilters}
      />

      <SelectedMoleculesBar
        count={selectedIds.length}
        isDownloading={selectedDownload.isDownloading}
        downloadProgress={selectedDownload.progress}
        onClearAll={clearSelection}
        onDownload={() => selectedDownload.trigger(selectedIds)}
      />

      {/* Sidebar + table */}
      <div className="flex min-h-0 flex-1 gap-3 overflow-hidden md:gap-6">
        <VirusFilterBar
          viruses={viruses}
          selectedViruses={selectedViruses}
          onToggle={handleVirusToggle}
          onClear={handleClearViruses}
        />

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
          {isFetching && !isPending && <UpdatingIndicator />}

          <div className="min-h-0 flex-1 overflow-hidden">
            <MolecularDataTable
              columns={columns}
              rows={data.data}
              selectedIds={selectedIds}
              onRowClick={handleRowClick}
            />
          </div>

          <PaginationFooter
            metadata={metadata}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            onPrevPage={() => setPage((p) => p - 1)}
            onNextPage={() => setPage((p) => p + 1)}
          />
        </div>
      </div>

      <FilterPanel
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        committed={panelState}
        rangeBounds={rangeBounds}
        onApply={handleApplyFilterPanel}
      />
    </div>
  );
}
